import { useEffect, useState } from "react";
import { IUser } from "../../models/IUser";
import { getUsersWithAdminRoles, assignAdminRole, removeAdminRole } from "../../api/users";
import { AdminRole, AdminRoleLabels } from "../../types/adminRoles";
import { useAppDispatch } from "../../store/store";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import AddUserRoleDialog from "../../components/AddUserRoleDialog";
import { CheckOctagon, DangerOctagon } from "@mynaui/icons-react";
import { UserPlus, X, Shield, Plus } from "lucide-react";
import moment from "moment";
import { Avatar, Button, Card, EmptyState, IconButton, Loader, PageHeader, Tag } from "../../ui";
import "./styles.scss";

const UserManagement = () => {
  const dispatch = useAppDispatch();
  const [users, setUsers] = useState<IUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [removingRole, setRemovingRole] = useState<{ userId: string; role: AdminRole } | null>(null);
  const [addingRoleToUser, setAddingRoleToUser] = useState<string | null>(null);
  const [assigningRole, setAssigningRole] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data } = await getUsersWithAdminRoles();
      setUsers(data);
    } catch (error: any) {
      console.error("Error fetching users:", error.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRoles = async (userId: string, roles: AdminRole[]) => {
    try {
      // Get user's existing roles
      const existingUser = users.find((u) => u.id === userId);
      const existingRoles = existingUser?.adminRoles || [];

      // Determine which roles to add
      const rolesToAdd = roles.filter((role) => !existingRoles.includes(role));

      // Assign each new role
      for (const role of rolesToAdd) {
        await assignAdminRole({ userId, role });
      }

      // Refresh the list
      await fetchUsers();

      dispatch(
        setGeneralAlertMessage({
          message: "Admin roles assigned successfully",
          type: "success",
        })
      );
    } catch (error: any) {
      dispatch(
        setGeneralAlertMessage({
          message: error.response?.data?.message || "Failed to assign roles",
          type: "error",
        })
      );
      throw error;
    }
  };

  const handleRemoveRole = async (userId: string, role: AdminRole) => {
    setRemovingRole({ userId, role });
    try {
      await removeAdminRole({ userId, role });

      // Update local state
      setUsers((prevUsers) => {
        const updatedUsers = prevUsers.map((u) => {
          if (u.id === userId) {
            const updatedRoles = (u.adminRoles || []).filter((r) => r !== role);
            return { ...u, adminRoles: updatedRoles };
          }
          return u;
        });

        // Remove user if they have no roles left
        return updatedUsers.filter((u) => u.adminRoles && u.adminRoles.length > 0);
      });

      dispatch(
        setGeneralAlertMessage({
          message: `Role "${AdminRoleLabels[role]}" removed successfully`,
          type: "success",
        })
      );
    } catch (error: any) {
      dispatch(
        setGeneralAlertMessage({
          message: error.response?.data?.message || "Failed to remove role",
          type: "error",
        })
      );
    } finally {
      setRemovingRole(null);
    }
  };

  const handleAddRoleToUser = async (userId: string, role: AdminRole) => {
    setAssigningRole(true);
    try {
      await assignAdminRole({ userId, role });

      // Update local state
      setUsers((prevUsers) =>
        prevUsers.map((u) => {
          if (u.id === userId) {
            return { ...u, adminRoles: [...(u.adminRoles || []), role] };
          }
          return u;
        })
      );

      setAddingRoleToUser(null);

      dispatch(
        setGeneralAlertMessage({
          message: `Role "${AdminRoleLabels[role]}" assigned successfully`,
          type: "success",
        })
      );
    } catch (error: any) {
      dispatch(
        setGeneralAlertMessage({
          message: error.response?.data?.message || "Failed to assign role",
          type: "error",
        })
      );
    } finally {
      setAssigningRole(false);
    }
  };

  const getAvailableRoles = (user: IUser): AdminRole[] => {
    const currentRoles = user.adminRoles || [];
    return (Object.values(AdminRole).filter((v) => typeof v === "number") as AdminRole[]).filter(
      (role) => !currentRoles.includes(role)
    );
  };

  useEffect(() => {
    document.title = "User Management";
    fetchUsers();
  }, []);

  return (
    <div className="user-management-page">
      <PageHeader
        title="User Management"
        subtitle={`${users.length} ${users.length === 1 ? "User" : "Users"} with Admin Roles`}
        actions={
          <Button icon={<UserPlus size={16} />} onClick={() => setIsAddDialogOpen(true)}>
            Add User
          </Button>
        }
      />

      {loading ? (
        <Loader />
      ) : users.length === 0 ? (
        <Card padding="none">
          <EmptyState
            icon={<Shield />}
            title="No Admin Users"
            description="Start by assigning administrative roles to users"
            action={
              <Button icon={<UserPlus size={16} />} onClick={() => setIsAddDialogOpen(true)}>
                Add User
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="um-grid">
          {users.map((user) => (
            <Card key={user.id} padding="none" className="um-card">
              <div className="um-card__head">
                <Avatar name={`${user.firstName} ${user.lastName ?? ""}`} src={user.photo} size={48} />
                <div className="um-card__who">
                  <div className="um-card__name-row">
                    <h3 className="um-card__name">
                      {user.firstName} {user.lastName}
                    </h3>
                    {user.emailVerified ? (
                      <CheckOctagon size={16} className="um-card__verified" />
                    ) : (
                      <DangerOctagon size={16} className="um-card__unverified" />
                    )}
                  </div>
                  <div className="um-card__email">{user.email}</div>
                  <div className="um-card__date">{moment(user.createdAt).format("DD MMM, YYYY")}</div>
                </div>
              </div>

              <div className="um-card__body">
                <div className="um-card__roles-head">
                  <p className="um-card__label">Admin Roles</p>
                  {getAvailableRoles(user).length > 0 && (
                    <IconButton
                      variant="tint"
                      size="sm"
                      label="Add role"
                      icon={<Plus size={14} />}
                      onClick={() => setAddingRoleToUser(addingRoleToUser === user.id ? null : user.id)}
                      disabled={assigningRole}
                    />
                  )}
                </div>
                <div className="um-card__roles">
                  {user.adminRoles?.map((role) => (
                    <Tag key={role} tone="brand" className="um-role">
                      {AdminRoleLabels[role]}
                      {/* A small ✕ inside the role chip */}
                      <button
                        type="button"
                        className="um-role__remove"
                        onClick={() => handleRemoveRole(user.id, role)}
                        disabled={removingRole?.userId === user.id && removingRole?.role === role}
                        data-tooltip="Remove role"
                        aria-label="Remove role"
                      >
                        <X size={12} />
                      </button>
                    </Tag>
                  ))}
                </div>

                {addingRoleToUser === user.id && (
                  <div className="um-card__add-roles">
                    {getAvailableRoles(user).map((role) => (
                      <Button
                        key={role}
                        variant="ghost"
                        size="sm"
                        block
                        className="um-card__add-role"
                        icon={<Plus size={14} />}
                        onClick={() => handleAddRoleToUser(user.id, role)}
                        disabled={assigningRole}
                      >
                        {AdminRoleLabels[role]}
                      </Button>
                    ))}
                  </div>
                )}
              </div>

              {user.nationality && (
                <div className="um-card__foot">
                  <span className="um-card__foot-label">Nationality:</span>
                  <span className="um-card__foot-value">{user.nationality}</span>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      <AddUserRoleDialog
        isOpen={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onAssign={handleAssignRoles}
      />
    </div>
  );
};

export default UserManagement;
